import { useState } from 'react';
import { ClientLogo } from '@/components/client-logo';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import {
  LanguageMenu,
  LanguageSwitcher,
} from '@/components/language-switcher';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLogin } from './auth.api';
import { LoginBrandPanel } from './login-brand-panel';
import { useAuthStore } from './auth.store';

/* Messages are i18n keys, translated where they are rendered — a resolver
   built once at module load would otherwise freeze them in the boot language. */
const schema = z.object({
  email: z
    .string()
    .min(1, 'auth.validation.emailRequired')
    .email('auth.validation.emailInvalid'),
  password: z.string().min(1, 'auth.validation.passwordRequired'),
});

type FormValues = z.infer<typeof schema>;

/**
 * Sign in.
 *
 * The old login was two bare inputs squeezed into the marketing header, which
 * meant the primary action of the whole portal was a 32px-tall control competing
 * with a logo and a language switcher. It also disabled the submit button until
 * both fields validated, so a typo left the user pressing a dead button with no
 * message. Here submission is always available and errors are stated.
 */
export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const location = useLocation();
  const login = useLogin();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/dashboard'} replace />;
  }

  return (
    <div className="grid lg:min-h-svh lg:grid-cols-2">
      {/* Form column */}
      <div className="flex min-h-[85svh] flex-col gap-8 p-6 sm:p-10 lg:min-h-0">
        <div className="flex items-center justify-between gap-4">
          {/* The client's own logo, with the monogram as the fallback while
              the config file loads or when a client has not set one. */}
          <ClientLogo className="h-8 max-w-[11rem]" />
          {/* Signed out there is no profile menu to hide this in, and the
              choice has to be reachable before you can read the form. */}
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            <LanguageMenu className="sm:hidden" />
            <ThemeToggle />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <div className="mb-8 space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t('auth.login.title')}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t('auth.login.subtitle')}
              </p>
            </div>

            <form
              noValidate
              onSubmit={handleSubmit((values) => login.mutate(values))}
              className="space-y-4"
            >
              {login.isError && (
                <div
                  role="alert"
                  className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-lg border p-3 text-sm"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>
                    {login.error instanceof Error
                      ? t(login.error.message)
                      : t('auth.login.genericError')}
                  </span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder={t('auth.login.emailPlaceholder')}
                  aria-invalid={Boolean(errors.email)}
                  disabled={login.isPending}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-destructive text-xs">
                    {t(errors.email.message!)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('common.password')}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
                  >
                    {t('auth.login.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="pr-10"
                    aria-invalid={Boolean(errors.password)}
                    disabled={login.isPending}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={
                      showPassword
                        ? t('auth.login.hidePassword')
                        : t('auth.login.showPassword')
                    }
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex w-10 items-center justify-center"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-destructive text-xs">
                    {t(errors.password.message!)}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t('auth.login.submitting')}
                  </>
                ) : (
                  <>
                    <LogIn />
                    {t('auth.login.submit')}
                  </>
                )}
              </Button>
            </form>

            <p className="text-muted-foreground mt-6 text-center text-sm">
              {t('auth.login.needAccount')}{' '}
              <Link
                to="/register"
                className="text-foreground font-medium underline-offset-4 hover:underline"
              >
                {t('auth.login.requestAccess')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <LoginBrandPanel />
    </div>
  );
}
